// EDN as far as a blueprint (RFC-055) writes it, for the /bank editor's
// fields (byj-1wv.90): maps, vectors, lists and sets, keywords, symbols,
// strings, numbers, true, false and nil, with comments and `#_` discards
// skipped. Each value keeps where it was written, so a key the fields do not
// know is kept as its own text. Anything else (a tagged literal, a
// character, a ratio) is refused, saying where, and the editor stays on the
// text.

/** A value read, and the span of the text it was read from. */
export type Edn = { start: number; end: number } & (
  | { t: "map"; entries: { key: Edn; value: Edn }[] }
  | { t: "vec" | "list" | "set"; items: Edn[] }
  | { t: "kw"; name: string }
  | { t: "sym"; name: string }
  | { t: "str"; value: string }
  | { t: "num"; value: number; raw: string }
  | { t: "bool"; value: boolean }
  | { t: "nil" }
);

/** What could not be read, and the offset it was found at. */
export class EdnError extends Error {
  constructor(
    message: string,
    readonly at: number,
  ) {
    super(message);
  }
}

const delimiter = /[\s,()[\]{}";]/;
const escapes: Record<string, string> = { '"': '"', "\\": "\\", n: "\n", t: "\t", r: "\r", b: "\b", f: "\f" };

/** The one value `text` writes, with only whitespace and comments around it. */
export function readEdn(text: string): Edn {
  let i = 0;
  const skip = () => {
    for (;;) {
      while (i < text.length && /[\s,]/.test(text[i]!)) i++;
      if (text[i] === ";") {
        while (i < text.length && text[i] !== "\n") i++;
      } else if (text.startsWith("#_", i)) {
        i += 2;
        value();
      } else return;
    }
  };
  const token = () => {
    const start = i;
    while (i < text.length && !delimiter.test(text[i]!)) i++;
    return text.slice(start, i);
  };
  const items = (close: string): Edn[] => {
    const out: Edn[] = [];
    for (;;) {
      skip();
      if (i >= text.length) throw new EdnError(`no closing ${close}`, i);
      if (text[i] === close) {
        i++;
        return out;
      }
      out.push(value());
    }
  };
  const value = (): Edn => {
    skip();
    const start = i;
    const c = text[i];
    if (c === undefined) throw new EdnError("nothing to read", i);
    if (c === "{" || c === "[" || c === "(" || text.startsWith("#{", i)) {
      const set = c === "#";
      i += set ? 2 : 1;
      const close = c === "[" ? "]" : c === "(" ? ")" : "}";
      const xs = items(close);
      if (c === "{") {
        if (xs.length % 2) throw new EdnError("a map with a key and no value", start);
        const entries = [];
        for (let k = 0; k < xs.length; k += 2) entries.push({ key: xs[k]!, value: xs[k + 1]! });
        return { t: "map", entries, start, end: i };
      }
      return { t: set ? "set" : c === "[" ? "vec" : "list", items: xs, start, end: i };
    }
    if (c === "}" || c === "]" || c === ")") throw new EdnError(`an unopened ${c}`, i);
    if (c === '"') {
      i++;
      let s = "";
      for (;;) {
        const d = text[i];
        if (d === undefined) throw new EdnError("a string with no end", start);
        i++;
        if (d === '"') return { t: "str", value: s, start, end: i };
        if (d !== "\\") {
          s += d;
          continue;
        }
        const e = text[i++];
        if (e === "u" && /^[0-9a-fA-F]{4}$/.test(text.slice(i, i + 4))) {
          s += String.fromCharCode(parseInt(text.slice(i, i + 4), 16));
          i += 4;
        } else if (e !== undefined && e in escapes) s += escapes[e];
        else throw new EdnError(`an unknown escape \\${e ?? ""}`, i - 2);
      }
    }
    if (c === "#") throw new EdnError("a tagged value, which a blueprint does not use", i);
    if (c === "\\") throw new EdnError("a character, which a blueprint does not use", i);
    const tok = token();
    if (c === ":") {
      if (tok.length < 2) throw new EdnError("a keyword with no name", start);
      return { t: "kw", name: tok.slice(1), start, end: i };
    }
    if (/^[+-]?\d/.test(tok)) {
      // 1. is 1.0 to Clojure's reader, as 1.e2 is 100.0
      if (!/^[+-]?\d+(\.\d*)?([eE][+-]?\d+)?M?$/.test(tok) && !/^[+-]?\d+N$/.test(tok)) throw new EdnError(`not a number: ${tok}`, start);
      return { t: "num", value: Number(tok.replace(/[MN]$/, "")), raw: tok, start, end: i };
    }
    if (tok === "true" || tok === "false") return { t: "bool", value: tok === "true", start, end: i };
    if (tok === "nil") return { t: "nil", start, end: i };
    return { t: "sym", name: tok, start, end: i };
  };
  const v = value();
  skip();
  if (i < text.length) throw new EdnError("more after the value", i);
  return v;
}

/** A string as EDN writes it. */
export const ednString = (s: string) =>
  `"${s.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\n/g, "\\n").replace(/\t/g, "\\t").replace(/\r/g, "\\r")}"`;

/** A keyword name's part as Clojure's reader takes one: not starting with a digit, no slash. */
const namePart = "[A-Za-z*+!_?<>=.\\-][\\w*+!_?<>=.\\-]*";
/** Whether `name` can be written as a keyword's name Clojure reads back: a part, or a namespace and a part around one slash. */
export const keywordName = (name: string) => new RegExp(`^${namePart}(/${namePart})?$`).test(name);
